import React, { useMemo } from "react";

import InitialDataListPage from "../components/InitialDataListPage";

const DEFAULT_IMAGE_MIME = "image/png";

const detectImageMime = (value) => {
  if (!value || typeof value !== "string") {
    return DEFAULT_IMAGE_MIME;
  }

  if (value.startsWith("data:image/")) {
    const match = value.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/);
    return match ? match[1] : DEFAULT_IMAGE_MIME;
  }

  const signature = value.slice(0, 20);
  if (signature.startsWith("/9j/") || signature.startsWith("/9J/")) {
    return "image/jpeg";
  }
  if (signature.startsWith("iVBOR")) {
    return "image/png";
  }
  if (signature.startsWith("R0lGOD")) {
    return "image/gif";
  }
  if (signature.startsWith("Qk")) {
    return "image/bmp";
  }
  return DEFAULT_IMAGE_MIME;
};

const getImageSrc = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }
  if (value.startsWith("data:image/")) {
    return value;
  }
  const mime = detectImageMime(value);
  return `data:${mime};base64,${value}`;
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  const numericValue = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(numericValue)) {
    return "-";
  }
  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const ItemMain = ({
  masterList = [],
  name,
  onPageInsert,
  onRowSelected,
  serverSide = false,
  totalItems,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  searchTerm,
  onSearchChange,
  isLoading = false,
  groups = [],
  filterGroup = null,
  onGroupChange,
}) => {
  const groupMap = useMemo(() => {
    const map = {};
    for (const g of groups) map[g.Group_Id] = g.Group_Name;
    return map;
  }, [groups]);

  const columns = useMemo(
    () => [
      {
        key: "index",
        header: "#",
        width: "6%",
        align: "center",
        render: (_item, _index, { startIndex, itemIndex }) =>
          startIndex + itemIndex + 1,
      },
      {
        key: "Item_Group",
        header: "หมวดหมู่",
        width: "14%",
        align: "center",
        render: (item) => groupMap[item.Item_Group] || "-",
      },
      {
        key: "Item_Img",
        header: "รูปสินค้า",
        width: "12%",
        align: "center",
        bodyClassName: "align-middle",
        render: (item) => {
          const imageSrc = getImageSrc(item.Item_Img);
          return imageSrc ? (
            <img
              src={imageSrc}
              alt={item.Item_Name || "รูปสินค้า"}
              style={{
                width: "52px",
                height: "52px",
                objectFit: "cover",
                borderRadius: "8px",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            />
          ) : (
            <span className="text-muted">-</span>
          );
        },
      },
      {
        key: "Item_Name",
        header: "ชื่อสินค้า",
        width: "24%",
        align: "start",
        render: (item) => item.Item_Name || "-",
      },
      {
        key: "Item_Short_Name",
        header: "ชื่อสินค้าแบบย่อ",
        width: "18%",
        align: "center",
        render: (item) => item.Item_Short_Name || "-",
      },
      {
        key: "Item_Unit_ST",
        header: "หน่วยสินค้าในคลัง",
        width: "9%",
        align: "center",
        render: (item) => item.Item_Unit_ST || "-",
      },
      {
        key: "Item_Cost",
        header: "ต้นทุนสินค้า",
        width: "12%",
        align: "center",
        render: (item) => formatMoney(item.Item_Cost),
      },
      {
        key: "Item_Price",
        header: "ราคาสินค้า",
        width: "12%",
        align: "center",
        render: (item) => formatMoney(item.Item_Price),
      },
      {
        key: "actions",
        header: "แก้ไข",
        width: "8%",
        align: "center",
        isAction: true,
        render: (item, _index, { triggerRowSelected }) => (
          <button
            onClick={(event) => {
              event.stopPropagation();
              triggerRowSelected(item);
            }}
            className="btn btn-sm"
            title="แก้ไข"
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: "#fff3e0",
              color: "#EF6C00",
              transition: "all 0.2s ease",
            }}
            onMouseOver={(event) => {
              event.currentTarget.style.backgroundColor = "#EF6C00";
              event.currentTarget.style.color = "#fff";
            }}
            onMouseOut={(event) => {
              event.currentTarget.style.backgroundColor = "#fff3e0";
              event.currentTarget.style.color = "#EF6C00";
            }}
            type="button"
          >
            <i className="fas fa-edit" />
          </button>
        ),
      },
    ],
    [groupMap]
  );

  const getSearchValues = useMemo(
    () => (item) => [
      item?.Item_Code,
      item?.Item_Name,
      item?.Item_Short_Name,
      item?.Item_Unit_ST,
      item?.Item_Cost,
      item?.Item_Price,
    ],
    []
  );

  const groupFilter = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 14, color: "#374151", whiteSpace: "nowrap" }}>หมวดหมู่สินค้า</span>
      <select
        value={filterGroup ?? ""}
        onChange={(e) => onGroupChange && onGroupChange(e.target.value === "" ? null : Number(e.target.value))}
        style={{ borderRadius: 6, border: "1px solid #d1d5db", padding: "6px 10px", fontSize: 14, minWidth: 160 }}
      >
        <option value="">ทั้งหมด</option>
        {groups.map((g) => (
          <option key={g.Group_Id} value={g.Group_Id}>{g.Group_Name}</option>
        ))}
      </select>
    </div>
  );

  return (
    <InitialDataListPage
      name={name}
      entityPath="/tbsetitem"
      data={masterList}
      serverSide={serverSide}
      totalItems={totalItems}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      onPageChange={onPageChange}
      onItemsPerPageChange={onItemsPerPageChange}
      searchTerm={searchTerm}
      onSearchChange={onSearchChange}
      isLoading={isLoading}
      onCreate={onPageInsert}
      onRowSelected={onRowSelected}
      columns={columns}
      getSearchValues={getSearchValues}
      searchPlaceholder={`ค้นหา${name || "ข้อมูลสินค้า"}...`}
      fieldMappings={{ no: "Item_Id" }}
      getRowId={(item) => item?.Item_Id}
      customHeader={groupFilter}
      hidePageTitle
    />
  );
};

export default ItemMain;
